<script setup>
import { computed, defineAsyncComponent, ref, watch, watchEffect } from 'vue'
import { useSettingsStore } from '@/store/settings'
import { useStorage } from '@vueuse/core'
import { useRoute } from 'vue-router'
import isEqual from 'lodash/isEqual'
import { startRegistration } from '@simplewebauthn/browser'
import api from '@/lib/api'
import { useToast } from 'primevue/usetoast'
const EditorTipTap = defineAsyncComponent(() =>
  import('@/components/EditorTipTap.vue')
)
const route = useRoute()
const settingsStore = useSettingsStore()
const pushNotificationStatus = ref()
const settingsTemp = ref()
const toast = useToast()
const loading = ref(false)
const selectedNotification = ref('newanomalytemplate')
const biometricLoginEnabled = useStorage('biometricLoginEnabled', false)
const fetchData = async () => {
  loading.value = true
  await settingsStore.initializeSettings()
  settingsTemp.value = JSON.parse(JSON.stringify(settingsStore.settings))
  pushNotificationStatus.value = Notification.permission
  loading.value = false
}

watch(() => route, fetchData, { immediate: true })

const newMail = ref('')
const inputMail = ref('')

const onFocusOut = (event) => {
  newMail.value = event.target.value
}

const onKeyPress = (event) => {
  if (event.key === 'Enter') {
    newMail.value = event.target.value
    if (newMail.value.length && validMail() && alreadyPresent()) manageMails('add', newMail.value)
  }
}

const manageMails = (action, mail) => {
  if (action === 'add') {
    settingsTemp.value.mails.push(mail)
    settingsTemp.value.mails = settingsTemp.value.mails.sort()
    newMail.value = ''
    inputMail.value = ''
  } else if (action === 'remove') {
    settingsTemp.value.mails = settingsTemp.value.mails.filter(el => el !== mail)
  }
}

const validMail = () => {
  return !newMail.value.length || /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(newMail.value)
}

const alreadyPresent = () => {
  return !settingsTemp.value.mails.includes(newMail.value)
}

const disableSave = computed(() => {
  return isEqual(JSON.stringify(settingsTemp.value), JSON.stringify(settingsStore.settings))
})

const saveSetting = async () => {
  loading.value = true
  await settingsStore.saveSettings(settingsTemp.value)
  settingsTemp.value = JSON.parse(JSON.stringify(settingsStore.settings))
  loading.value = false
}

const editMode = ref(false)
const selectedTemplate = ref('newanomalytemplate')
watch(() => editMode.value, (v, oldV) => {
  if (oldV && !v) settingsTemp.value[selectedTemplate.value].mail = templateEmailDrafts.value
})

const templateEmailDrafts = ref()
watchEffect(() => {
  templateEmailDrafts.value = settingsTemp.value?.[selectedTemplate.value].mail
})

const startRegistrationBtnClick = async () => {
  const optionsJSON = await api.webauthn.getRegistrationOptions()
  console.log(optionsJSON)
  let attResp
  try {
    attResp = await startRegistration({ optionsJSON })
    const verificationJSON = await api.post('/webauthn/verify-registration', attResp)
    if (verificationJSON && verificationJSON.verified) {
      toast.add({ severity: 'success', summary: 'Success!', detail: 'Biometric login has been activated', group: 'tr' })
      biometricLoginEnabled.value = true
    } else {
      toast.add({ severity: 'error', summary: 'Error!', detail: 'Oh no, something went wrong during Biometric login activation', group: 'tr' })
      console.error(`Oh no, something went wrong! Response: ${JSON.stringify(
        verificationJSON
      )}`)
    }
  } catch (error) {
    if (error.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') {
      toast.add({ severity: 'success', summary: 'Success!', detail: 'Biometric login has been activated', group: 'tr' })
      biometricLoginEnabled.value = true
    } else {
      toast.add({ severity: 'error', summary: 'Error!', detail: 'Oh no, something went wrong during Biometric login activation', group: 'tr' })
      console.error(error)
      throw error
    }
  }
}

function urlB64ToUint8Array (base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

const enablePushNotifications = async () => {
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    toast.add({ severity: 'error', summary: 'Error!', detail: 'Push notifications are not supported on this device', group: 'tr' })
  } else {
    pushNotificationStatus.value = await Notification.requestPermission()
    if (pushNotificationStatus.value === 'granted') {
      const config = { pushKey: 'BGPteWO667jnQNY_ktCuFtPXL_2V-ZLsk_xdHsx1yJwfP4eC7CaezQv4cvp8ePL8DrzjIGnD9CLfqg7P_utrQ1U' }
      const swReg = await navigator.serviceWorker.getRegistration()
      const subscription = await swReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(config.pushKey)
      })
      await api.post('/webpush/register', JSON.parse(JSON.stringify(subscription)))
      toast.add({ severity: 'success', summary: 'Success!', detail: 'Web push notifications have been activated', group: 'tr' })
      console.log(JSON.stringify(subscription))
    }
    pushNotificationStatus.value = Notification.permission
  }
}
</script>

<template>
  <div
    v-if="loading"
    class="card flex flex-1 w-full items-center justify-center"
  >
    <ProgressSpinner />
  </div>

  <div
    v-else
    class="flex flex-col gap-4"
  >
    <div
      v-if="!disableSave"
      class="absolute bottom-6 right-6 z-10"
    >
      <Button
        type="button"
        :rounded="true"
        size="large"
        class="scale-150"
        icon="pi pi-save"
        @click="saveSetting()"
      />
    </div>

    <div class="card flex flex-col gap-4">
      <h1 class="font-semibold text-2xl">
        General
      </h1>
      <Divider
        align="left"
        type="solid"
      >
        <b>Biometric Recognition</b>
      </Divider>
      <div class="font-medium flex flex-row justify-between w-full items-center gap-4">
        <p>Enable biometric recognition for future access to this device</p>
        <div v-if="biometricLoginEnabled">
          <Message
            severity="success"
            variant="simple"
            class="py-2 px-3"
          >
            Granted
          </Message>
        </div>
        <div v-else>
          <Button
            variant="text"
            @click="startRegistrationBtnClick"
          >
            Enable
          </Button>
        </div>
      </div>
      <Divider
        align="left"
        type="solid"
      >
        <b>Automatic Deletion</b>
      </Divider>
      <div class="font-medium flex flex-row justify-start w-full items-center gap-2">
        <div>
          Automatically delete logs data after
        </div>
        <div>
          <InputNumber
            v-model="settingsTemp.daysfordeletelogs"
            input-id="hours"
            :input-style="{ width: '4rem' }"
            show-buttons
            size="small"
            :min="15"
          />
        </div>
        <div>
          days
        </div>
      </div>
    </div>
    <div class="card flex flex-col gap-2">
      <h1 class="font-semibold text-2xl">
        Notifications & Mails
      </h1>
      <Divider
        align="left"
        type="solid"
      >
        <b>Push Notifications</b>
      </Divider>
      <div class="font-medium flex flex-row justify-between w-full items-center gap-4">
        <p>Enable the receipt of push notifications on this device</p>

        <div v-if="pushNotificationStatus === 'granted'">
          <Message
            severity="success"
            variant="simple"
            class="py-2 px-3"
          >
            Granted
          </Message>
        </div>
        <div v-else-if="pushNotificationStatus === 'denied'">
          <Message
            severity="error"
            variant="simple"
            class="py-2 px-3"
          >
            Denied
          </Message>
        </div>
        <div v-else>
          <Button
            variant="text"
            @click="enablePushNotifications"
          >
            Enable
          </Button>
        </div>
      </div>
      <Divider
        align="left"
        type="solid"
      >
        <b>Notification text</b>
      </Divider>
      <div class="font-medium flex flex-col justify-start w-full items-center gap-6">
        <div class="flex flew-row justify-start w-full gap-2">
          <div
            v-for="category in [
              { label: 'Start', key: 'newanomalynotification', value: 'newanomalytemplate'},
              { label: 'Going On', key: 'periodicanomalynotification', value: 'periodicanomalytemplate'},
              { label: 'End', key: 'closeanomalynotification', value: 'closeanomalytemplate'},
            ]"
            :key="category.key"
            class="flex items-center gap-1"
          >
            <RadioButton
              v-model="selectedNotification"
              :input-id="category.key"
              name="dynamic"
              :value="category.value"
            />
            <label
              :for="category.key"
              class="font-semibold"
            >{{ category.label }}</label>
          </div>
        </div>
        <div class="flex flex-row gap-2 w-full items-start">
          <div class="flex flex-col gap-4 w-full">
            <FloatLabel
              class="w-full"
              variant="on"
            >
              <InputText
                id="notification_title"
                v-model="settingsTemp[selectedNotification].notification.title"
                class="w-full"
              />
              <label for="notification_title">Title</label>
            </FloatLabel>
            <FloatLabel
              class="w-full"
              variant="on"
            >
              <InputText
                id="notification_message"
                v-model="settingsTemp[selectedNotification].notification.body"
                class="w-full"
              />
              <label for="notification_message">Body</label>
            </FloatLabel>
          </div>
        </div>
      </div>
      <Divider
        align="left"
        type="solid"
      >
        <b>Recipients</b>
      </Divider>
      <div class="font-medium flex gap-2 flex-col">
        Email addresses to which notifications relating to anomalies must be sent
        <div class="flex lg:flex-row flex-col gap-2 justify-between h-fit">
          <div class="flex flex-col gap-2 w-full">
            <FloatLabel variant="on">
              <IconField>
                <InputText
                  id="on_label"
                  v-model="inputMail"
                  v-keyfilter.email
                  fluid
                  class="w-full"
                  @keypress="onKeyPress($event)"
                  @focusout="onFocusOut($event)"
                />
                <InputIcon class="pi pi-envelope" />
              </IconField>
              <label for="on_label">New Mail</label>
            </FloatLabel>
            <Message
              v-if="!validMail()"
              severity="error"
              closable
              variant="simple"
            >
              Wrong format.
            </Message>
            <Message
              v-if="!alreadyPresent()"
              severity="error"
              closable
              variant="simple"
            >
              Mail already present.
            </Message>
            <div class="flex flex-row gap-2">
              <div
                v-for="mail of settingsTemp.mails"
                :key="mail"
              >
                <Chip
                  :label="mail"
                  removable
                  @remove="manageMails('remove', mail)"
                />
              </div>
            </div>
          </div>
        </div>
        <Divider
          align="left"
          type="solid"
        >
          <b>Reminder Interval</b>
        </Divider>
        <div class="font-medium flex flex-row justify-start w-full items-center gap-2">
          <div>
            Send reminder emails every
          </div>
          <div>
            <InputNumber
              v-model="settingsTemp.intervalmails.hours"
              input-id="hours"
              :input-style="{ width: '4rem' }"
              show-buttons
              size="small"
              :min="0"
              :max="23"
            />
          </div>
          <div>
            hours
          </div>
          <div>
            <InputNumber
              v-model="settingsTemp.intervalmails.minutes"
              input-id="minutes"
              show-buttons
              size="small"
              :min="0"
              :input-style="{ width: '4rem' }"
              :max="59"
            />
          </div>
          <div>
            minutes
          </div>
        </div>
        <Divider
          align="left"
          type="solid"
        >
          <b>Email Templates</b>
        </Divider>
        <div class="font-medium flex flex-col justify-start w-full items-center gap-6">
          <div class="flex flew-row justify-start w-full gap-2">
            <div
              v-for="category in [
                { label: 'Start', key: 'newanomalymail', value: 'newanomalytemplate'},
                { label: 'Going On', key: 'periodicanomalymail', value: 'periodicanomalytemplate'},
                { label: 'End', key: 'closeanomalymail', value: 'closeanomalytemplate'},
              ]"
              :key="category.key"
              class="flex items-center gap-1"
            >
              <RadioButton
                v-model="selectedTemplate"
                :input-id="category.key"
                name="dynamic"
                :value="category.value"
              />
              <label
                :for="category.key"
                class="font-semibold"
              >{{ category.label }}</label>
            </div>
          </div>
          <div class="flex flex-row gap-2 w-full items-start">
            <div class="flex flex-col gap-4 w-full">
              <FloatLabel
                class="w-full"
                variant="on"
              >
                <InputText
                  id="mail_sujbect"
                  v-model="templateEmailDrafts.subject"
                  class="w-full"
                />
                <label for="mail_sujbect">Subject</label>
              </FloatLabel>
              <div
                v-if="Object.keys(selectedTemplate)?.length"
                class="flex-grow relative dark:border-gray-600"
                :class="{border : !editMode}"
              >
                <div
                  v-if="!editMode"
                  style="position: absolute; right: 3px; top: 3px;"
                >
                  <Button
                    icon="pi pi-pencil"
                    rounded
                    text
                    @click="editMode = true"
                  />
                </div>
                <div
                  v-else
                  style="position: absolute; right: 3px; top: 42px;"
                >
                  <Button
                    text
                    icon="pi pi-check"
                    @click="editMode = false"
                  />
                </div>
                <editorTipTap
                  v-model="templateEmailDrafts.body"
                  :editable="editMode"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
